import React, { useMemo } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ResponsiveRows, ResponsiveRowsColumn, SectionCard, StatusChip, StatusTone } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useHelpCenterEnabled } from '../../hooks/useHelpCenterEnabled';
import type { IntegrationConfig } from '../../services/integrationApi';
import { platformDisplayLabel } from '../../utils/storePlatform';
import {
  changeOutcome,
  createOutcome,
  createsProducts,
  effectiveFieldWrite,
  eventOutcome,
  EffectiveFieldWrite,
  fieldNoteKey,
  isSyncBehaviorPlatform,
  reconcilesOwned,
  removalOutcome,
  resolveSyncMode,
  SYNC_BEHAVIOR,
  SyncFieldCell,
} from '../../constants/syncBehavior';

/** Help Center article this section summarises (`docs/customer/getting-started/what-motzu-syncs.md`). */
export const WHAT_MOTZU_SYNCS_ARTICLE_PATH = '/help/getting-started/what-motzu-syncs';

const T = 'integrations.whatMotzuSyncs';

const WRITE_TONE: Record<EffectiveFieldWrite, StatusTone> = {
  replace: 'info',
  add: 'info',
  fill_blank: 'neutral',
  price_runs: 'neutral',
  untouched: 'neutral',
  off: 'neutral',
  switched_off: 'neutral',
  kept: 'positive',
};

interface Row {
  cell: SyncFieldCell;
  write: EffectiveFieldWrite;
  create: 'yes' | 'no' | 'none';
  /** Motzu takes back what it added and no longer sends (flag `shopify_owned_field_cleanup`). */
  reconciles: boolean;
}

/**
 * "What Motzu syncs" on an integration's Sync behavior tab: a plain summary of what a sync
 * writes, never touches and does when something changes, then one row per field for this
 * integration's saved mode and choices. Read-only; it explains the push, it changes nothing.
 */
const WhatMotzuSyncsSection: React.FC<{ integration: IntegrationConfig }> = ({ integration }) => {
  const { t } = useAppTranslation();
  const parity = useOrgFeatureFlag('lightspeed_catalog_parity');
  const linkOnly = useOrgFeatureFlag('lightspeed_link_only');
  const storeConfig = useOrgFeatureFlag('lightspeed_store_config');
  const initialSyncMode = useOrgFeatureFlag('initial_sync_mode');
  const listingShape = useOrgFeatureFlag('shopify_listing_shape');
  const metafieldSwitches = useOrgFeatureFlag('shopify_metafield_switches');
  const ownedFieldCleanup = useOrgFeatureFlag('shopify_owned_field_cleanup');
  const unsyncCleanup = useOrgFeatureFlag('shopify_unsync_cleanup');
  const respectStoreDeletes = useOrgFeatureFlag('shopify_respect_store_deletes');
  const enrichFillOnly = useOrgFeatureFlag('enrich_fill_only');
  const photoTopUp = useOrgFeatureFlag('lightspeed_photo_topup');
  const { enabled: helpCenterEnabled } = useHelpCenterEnabled();
  const platform = integration.platform;
  const settings = integration.settings;

  const flags = useMemo(
    () => ({
      lightspeedCatalogParity: parity.enabled,
      lightspeedLinkOnly: linkOnly.enabled,
      lightspeedStoreConfig: storeConfig.enabled,
      initialSyncMode: initialSyncMode.enabled,
      shopifyListingShape: listingShape.enabled,
      shopifyMetafieldSwitches: metafieldSwitches.enabled,
      shopifyOwnedFieldCleanup: ownedFieldCleanup.enabled,
      shopifyUnsyncCleanup: unsyncCleanup.enabled,
      shopifyRespectStoreDeletes: respectStoreDeletes.enabled,
      enrichFillOnly: enrichFillOnly.enabled,
      lightspeedPhotoTopUp: photoTopUp.enabled,
    }),
    [
      parity.enabled,
      linkOnly.enabled,
      storeConfig.enabled,
      initialSyncMode.enabled,
      listingShape.enabled,
      metafieldSwitches.enabled,
      ownedFieldCleanup.enabled,
      unsyncCleanup.enabled,
      respectStoreDeletes.enabled,
      enrichFillOnly.enabled,
      photoTopUp.enabled,
    ],
  );

  const rows = useMemo<Row[]>(() => {
    if (!isSyncBehaviorPlatform(platform)) return [];
    return SYNC_BEHAVIOR[platform].fields.map((cell) => ({
      cell,
      write: effectiveFieldWrite(platform, cell, settings, flags),
      create: createOutcome(platform, cell, settings, flags),
      reconciles: reconcilesOwned(platform, cell, settings, flags),
    }));
  }, [platform, settings, flags]);

  if (!isSyncBehaviorPlatform(platform)) return null;
  const behavior = SYNC_BEHAVIOR[platform];
  const platformLabel = platformDisplayLabel(platform, t);
  const mode = resolveSyncMode(platform, settings, flags);
  const modeFolded = platform === 'lightspeed' && (settings?.syncMode ?? 'override') !== mode;
  const creates = createsProducts(platform, settings, flags);

  const fieldLabel = (row: Row) => t(`${T}.fields.${row.cell.field}`);
  const writes = rows.filter(
    (row) => row.write === 'replace' || row.write === 'add' || row.write === 'fill_blank' || row.write === 'price_runs',
  );
  const never = rows.filter((row) => !writes.includes(row));
  const list = (items: Row[]) => (items.length > 0 ? items.map(fieldLabel).join(', ') : t(`${T}.summary.none`));

  const columns: ResponsiveRowsColumn[] = [
    { key: 'field', header: t(`${T}.columns.field`), width: 'minmax(0,1.6fr)' },
    { key: 'create', header: t(`${T}.columns.newProduct`), width: 'minmax(0,0.8fr)', mobileLabel: true },
    { key: 'sync', header: t(`${T}.columns.eachSync`), width: 'minmax(0,0.9fr)', mobileLabel: true },
    { key: 'changed', header: t(`${T}.columns.changed`), width: 'minmax(0,1fr)', mobileLabel: true },
    { key: 'removed', header: t(`${T}.columns.removed`), width: 'minmax(0,1fr)', mobileLabel: true },
  ];

  const renderCell = (row: Row, key: string): React.ReactNode => (
    <Box data-testid={`sync-field-${row.cell.field}-${key}`} sx={{ minWidth: 0 }}>
      {renderCellContent(row, key)}
    </Box>
  );

  const renderCellContent = (row: Row, key: string): React.ReactNode => {
    switch (key) {
      case 'field': {
        const noteKey = row.reconciles && row.cell.ownedCleanupNoteKey ? row.cell.ownedCleanupNoteKey : fieldNoteKey(row.cell, flags);
        return (
          <>
            <Typography variant="body2" fontWeight={600}>
              {fieldLabel(row)}
            </Typography>
            {noteKey && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
                {t(`${T}.notes.${noteKey}`)}
              </Typography>
            )}
          </>
        );
      }
      case 'create':
        return (
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.create.${row.create}`)}
          </Typography>
        );
      case 'sync':
        return <StatusChip label={t(`${T}.write.${row.write}`)} tone={WRITE_TONE[row.write]} />;
      case 'changed':
        return (
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.change.${row.reconciles ? changeOutcome(row.cell, row.write, true) : changeOutcome(row.cell, row.write)}`)}
          </Typography>
        );
      case 'removed':
        return (
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.removed.${row.reconciles ? removalOutcome(row.cell, row.write, true) : removalOutcome(row.cell, row.write)}`)}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <SectionCard
      dense
      title={t(`${T}.title`)}
      subtitle={t(`${T}.subtitle`, { platform: platformLabel })}
      actions={
        helpCenterEnabled ? (
          <Link component={RouterLink} to={WHAT_MOTZU_SYNCS_ARTICLE_PATH} variant="body2" underline="hover">
            {t(`${T}.guideLink`)}
          </Link>
        ) : undefined
      }
    >
      <Box sx={{ p: '16px 20px' }} data-testid="what-motzu-syncs-summary">
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          {behavior.syncPaused
            ? t(`${T}.notSyncingYet`, { platform: platformLabel })
            : behavior.honoursSyncMode
              ? t(`${T}.mode`, { mode: t(`${T}.modes.${mode}`) })
              : t(`${T}.noModes`, { platform: platformLabel })}
        </Typography>
        {!creates && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(`${T}.summary.noCreate`)}
          </Typography>
        )}
        {modeFolded && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(`${T}.modeFolded`)}
          </Typography>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary" component="h3">
              {t(`${T}.summary.writes`)}
            </Typography>
            <Typography variant="body2">{list(writes)}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" component="h3">
              {t(`${T}.summary.never`)}
            </Typography>
            <Typography variant="body2">{list(never)}</Typography>
          </Box>
        </Box>
        <Typography variant="overline" color="text.secondary" component="h3">
          {t(`${T}.summary.changes`)}
        </Typography>
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          {behavior.events.map((event) => (
            <Typography component="li" variant="body2" key={event.event}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {t(`${T}.events.${event.event}`)}:
              </Box>{' '}
              {t(`${T}.events.outcomes.${eventOutcome(platform, event, settings, flags)}`)}
            </Typography>
          ))}
        </Stack>
      </Box>
      <ResponsiveRows<Row>
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.cell.field}
        renderCell={renderCell}
        padding="12px 20px"
        rowDivider="top"
      />
    </SectionCard>
  );
};

export default WhatMotzuSyncsSection;
